import React, { useEffect } from 'react';
import { getOpenHoursText, getAddressText } from '@src/utills/utils';
import { capitalizeSentenceFirstLetters, formatCostField } from '@repo/utils';
import { renderToString } from 'react-dom/server';
import { AbyssProvider } from '@uhg-abyss/web/ui/AbyssProvider';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { FindhelpProgram } from '@src/api.types';
import { CboCardFindhelpNextSteps } from '@repo/ui';

type EmailBodyProps = {
  program: FindhelpProgram;
  updateEmailBody: (emailObj: any) => void;
};

export const EmailBody = ({ program, updateEmailBody }: EmailBodyProps) => {
  const cost = formatCostField(program?.free_or_reduced);
  const addressText = getAddressText(program);
  const footer =
    'This resource was shared by United Healthcare Community Resources, powered by Findhelp.';

  const NextStepsElement = () => {
    return (
      <CboCardFindhelpNextSteps
        nextStepsData={program.next_steps}
        firstOfficeAddressText={addressText}
        firstOfficeEmail={program.offices[0]?.email ?? ''}
        firstOfficePhone={program.offices[0]?.phone_number ?? ''}
        firstOfficeWebsite={program.offices[0]?.website_url ?? ''}
        source='member' // fixes bug which generates second "next steps:"
      />
    );
  };

  function getMainServices(): string {
    return program.service_tags.length > 0
      ? program.service_tags
        .map((tag) => {
          return capitalizeSentenceFirstLetters(tag);
        })
        .join(', ')
      : 'No main services information available';
  }

  function getCoverageText(): string {
    return (
      program.coverage_description ??
      'No coverage area information is available'
    );
  }
  function getAttributeTags(): string {
    return program.attribute_tags.length > 0
      ? program.attribute_tags
        .map((tag) => {
          return capitalizeSentenceFirstLetters(tag);
        })
        .join(', ')
      : 'No serving information available';
  }
  function getEligibility(): string {
    return program?.rules?.length
      ? program.rules.join(' ')
      : 'No eligibility information is available';
  }

  function getAvailability(): string {
    return program.availability || 'No availability information is available';
  }

  function getLanguages(): string {
    return program.supported_languages.length > 0
      ? program.supported_languages
        .map((language) => {
          return capitalizeSentenceFirstLetters(language);
        })
        .join(', ')
      : 'No language information is available';
  }

  const emailObj = {
    name: program.name,
    description: program.description,
    provider: program.provider_name,
    mainServices: getMainServices(),
    serving: getAttributeTags(),
    eligibility: getEligibility(),
    cost,
    languages: getLanguages(),
    availability: getAvailability(),
    coverageArea: getCoverageText(),
    location: addressText,
    distance: program.distance,
    // FIXME - Where was this getting set? Why do we need this *and* the street address above?
    // zipcode: program.zipcode,
    hours: {
      sunday: getOpenHoursText(program.offices[0], 'sunday'),
      monday: getOpenHoursText(program.offices[0], 'monday'),
      tuesday: getOpenHoursText(program.offices[0], 'tuesday'),
      wednesday: getOpenHoursText(program.offices[0], 'wednesday'),
      thursday: getOpenHoursText(program.offices[0], 'thursday'),
      friday: getOpenHoursText(program.offices[0], 'friday'),
      saturday: getOpenHoursText(program.offices[0], 'saturday'),
    },
    nextStep: renderToString(<AbyssProvider theme={createTheme('uhc')}><NextStepsElement /></AbyssProvider>),
    footer,
  };

  useEffect(() => {
    updateEmailBody(emailObj);
  }, [emailObj]);

  return '';
};
